import React, { FC } from "react"
import { DateTime } from "luxon"

import { BookFragmentFragment, PostBookFragmentFragment } from "@graphqlTypes"

import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import Avatar, { AVATAR_SIZES } from "@components/Avatar/Avatar"
import AvatarsRow from "@components/AvatarsRow/AvatarsRow"
import Content from "@components/Content/Content"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"

import { PostBookHeroHERO } from "./PostBookHero.styles"

import { POST_BOOK_THEME_STATE } from "../../PostBook"

const PostBookHero: FC<{
  authors: PostBookFragmentFragment["authors"]
  book?: BookFragmentFragment | null
  date: PostBookFragmentFragment["date"]
  image: PostBookFragmentFragment["image"]
  introduction: PostBookFragmentFragment["introduction"]
  permalink: PostBookFragmentFragment["permalink"]
  title: PostBookFragmentFragment["title"]
}> = ({
  authors,
  book,
  date,
  image,
  introduction,
  title,
}) => (
    <PostBookHeroHERO className={`PostBookHero theme-state--${POST_BOOK_THEME_STATE.ACTIVE}`}>
      <ImageAboveFold
        alt={`Image: ${title}`}
        aspectRatio={16 / 9}
        imageData={image?.imgixImage?.gatsbyImageData}
      />
      <div className="container">
        <div className="grid">
          <div className="column column--1-13-L">
            <div className="PostBookHero--breadcrumbs type--text-S font-weight--light">
              <AnchorLink href="/resources/">Resources</AnchorLink>
              {book?.posts && book.posts[0] && (
                <>
                  <Icon
                    color="var(--PostBook--text-color)"
                    size="var(--font-size--L)"
                    type={ICON_TYPES["chevron-right"]}
                  />
                  <AnchorLink href={book.posts[0].permalink || "/blog/"}>{book.title}</AnchorLink>
                </>
              )}
              <Icon
                color="var(--PostBook--text-color)"
                size="var(--font-size--L)"
                type={ICON_TYPES["chevron-right"]}
              />
            </div>
            <h1 className="type--headline-medium-7XL font-weight--medium">{title}</h1>
            {authors && (
              <div className={`PostBookHero--authors type--text-S${introduction ? " margin-bottom--M" : " margin-bottom--0"}`}>
                <AvatarsRow>
                  {authors.map(author => !(author?.name && author?.image) ? null : (
                    <AnchorLink
                      key={author.name}
                      href={author.permalink || "/"}
                    >
                      <Avatar
                        image={author.image}
                        name={author.name}
                        size={AVATAR_SIZES.S}
                      />
                    </AnchorLink>
                  ))}
                </AvatarsRow>
                <span className="font-weight--medium">
                  {authors.length > 1 ? "Multiple collaborators" : authors[0]?.name}
                </span>
                <span>•</span>
                <span>
                  {DateTime.fromISO(date).toFormat("LLLL d, yyyy")}
                </span>
              </div>
            )}
            {introduction && (
              <div className="PostBookHero--introduction">
                <Content
                  className="introduction"
                  content={introduction}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PostBookHeroHERO>
  )

export default PostBookHero
